import React, { useEffect, useState } from 'react';
import { Bookmark, BookmarkCheck, Check, Hash, Newspaper } from 'lucide-react';
import { articleService } from '../../services/api';
import { courseShort, shortDate } from '../../utils/articles';
import './articles.css';

/** Capa do artigo ou, sem imagem (ou se ela não carregar), o padrão hachurado. */
export function ArticleCover({ article, className = '' }) {
  const [broken, setBroken] = useState(false);
  if (article?.coverUrl && !broken) {
    return <img className={`art-cover ${className}`} src={article.coverUrl} alt="" loading="lazy" onError={() => setBroken(true)} />;
  }
  return (
    <span className={`art-cover art-cover--empty ${className}`} aria-hidden="true">
      <Newspaper size={20} strokeWidth={1.8} />
    </span>
  );
}

/** Linha "#TEMA · ● CURSO". */
export function ArticleMeta({ article, max = 1, onDark = false }) {
  const tags = (article.tags || []).slice(0, max);
  const course = article.courses?.[0];
  return (
    <span className={`art-meta ${onDark ? 'art-meta--dark' : ''}`}>
      {tags.map(t => (
        <span key={t.slug} className="art-meta__tag"><Hash size={11} strokeWidth={2.6} aria-hidden="true" />{t.name}</span>
      ))}
      {course && (
        <>
          <span className="art-meta__sep" aria-hidden="true" />
          <span className="art-meta__course">
            <span className="art-meta__dot" style={{ background: course.color || 'var(--navy)' }} aria-hidden="true" />
            {courseShort(course.name)}
          </span>
        </>
      )}
    </span>
  );
}

export function ReadBadge() {
  return <span className="art-read"><Check size={12} strokeWidth={3} aria-hidden="true" />Lido</span>;
}

/** Salvar/desalvar com atualização otimista. */
export function SaveToggle({ article, onChange, className = '' }) {
  const [saved, setSaved] = useState(Boolean(article.saved));
  const toggle = async (e) => {
    e.stopPropagation();
    const next = !saved;
    setSaved(next);
    try {
      const r = await articleService.toggleSave(article.id);
      setSaved(r.saved);
      onChange?.(r.saved);
    } catch {
      setSaved(!next);
    }
  };
  const Icon = saved ? BookmarkCheck : Bookmark;
  return (
    <button
      type="button"
      className={`art-save ${saved ? 'is-saved' : ''} ${className}`}
      onClick={toggle}
      aria-pressed={saved}
      aria-label={saved ? 'Remover dos salvos' : 'Salvar artigo'}
      title={saved ? 'Salvo' : 'Salvar'}
    >
      <Icon size={18} aria-hidden="true" />
    </button>
  );
}

/** Linha do feed (lista de artigos e página do tema). */
export function ArticleRow({ article, onOpen, showStatus = false, tour }) {
  const draft = article.status === 'draft';
  return (
    <div
      className="art-row"
      data-tour={tour}
      role="link"
      tabIndex={0}
      onClick={() => onOpen(article)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(article)}
    >
      <ArticleCover article={article} className="art-row__cover" />
      <div className="art-row__text">
        <ArticleMeta article={article} />
        <span className="art-row__title">{article.title || 'Sem título'}</span>
        {article.subtitle && <span className="art-row__excerpt">{article.subtitle}</span>}
        <span className="art-row__by">
          <span><strong>{article.author?.name}</strong> · {shortDate(article.publishedAt || article.updatedAt)} · {article.readingMinutes} min</span>
          {article.read && <ReadBadge />}
          {showStatus && draft && <span className="art-draft">Rascunho</span>}
        </span>
      </div>
      {!draft && <SaveToggle article={article} />}
    </div>
  );
}

/** Card com capa (tela inicial, visão geral do curso). */
export function ArticleCard({ article, onOpen, className = '' }) {
  return (
    <div
      className={`art-card ${className}`}
      role="link"
      tabIndex={0}
      onClick={() => onOpen(article)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(article)}
    >
      <ArticleCover article={article} className="art-card__cover" />
      <div className="art-card__body">
        <ArticleMeta article={article} />
        <span className="art-card__title">{article.title}</span>
        <span className="art-card__by"><strong>{article.author?.name}</strong> · {article.readingMinutes} min</span>
      </div>
    </div>
  );
}

/**
 * Páginas públicas abrem sempre no tema escuro. O ThemeProvider aplica o tema depois
 * (efeitos do pai rodam após os do filho), então a página segura o data-theme enquanto
 * estiver montada e, ao sair, devolve o último tema que ele pediu.
 */
export function useForcedDarkTheme() {
  useEffect(() => {
    const root = document.documentElement;
    let wanted = root.getAttribute('data-theme');
    const hold = () => {
      const current = root.getAttribute('data-theme');
      if (current === 'dark') return;
      wanted = current;
      root.setAttribute('data-theme', 'dark');
    };
    hold();
    const observer = new MutationObserver(hold);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      observer.disconnect();
      if (wanted) root.setAttribute('data-theme', wanted);
      else root.removeAttribute('data-theme');
    };
  }, []);
}

/** Pílula de tema com contagem (filtros e laterais). */
export function TagPill({ tag, count, active = false, onClick, size = 'md' }) {
  return (
    <button
      type="button"
      className={`art-pill art-pill--${size} ${active ? 'is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <span className="art-pill__hash"><Hash size={11} strokeWidth={2.6} aria-hidden="true" /></span>
      {tag}
      {count != null && <span className="art-pill__count">{count}</span>}
    </button>
  );
}
